import { lazy } from "react";

const RELOAD_AT_KEY = "chunkReloadAt";
// If a reload doesn't fix the chunk within this window, we already tried and the
// chunk is genuinely gone (bad/partial deploy) -> stop reloading, surface the
// error. A time box (not a one-shot flag) is what prevents a loop in BOTH
// directions: a stale landing chunk that keeps failing after its reload, and a
// broken non-landing chunk the user keeps navigating back to.
const RELOAD_COOLDOWN_MS = 10000;
// A blocked reload (sandboxed/embedded WebView, e.g. an email link-preview) never
// navigates, so fall through to the error boundary after this grace period
// instead of hanging on the Suspense fallback forever.
const RELOAD_GRACE_MS = 4000;

function readReloadAt() {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_AT_KEY)) || 0;
  } catch {
    return 0;
  }
}

function markReloadAt(value) {
  try {
    window.sessionStorage.setItem(RELOAD_AT_KEY, String(value));
  } catch {
    // Sandboxed contexts block storage. We then can't dedupe across reloads, but
    // a blocked reload also no-ops and falls through the grace timeout below, so
    // this degrades to "show the error boundary", never an infinite loop.
  }
}

function isChunkLoadError(error) {
  const name = error?.name || "";
  const message = error?.message || "";
  return (
    name === "ChunkLoadError" ||
    error?.code === "CSS_CHUNK_LOAD_FAILED" ||
    /Loading (CSS )?chunk [^\s]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message)
  );
}

// Deploy-aware loader for a dynamic import. Webpack content-hashes every chunk
// filename and a deploy deletes the previous build's files, so a tab holding a
// stale index.html 404s the moment it lazy-loads a chunk -> ChunkLoadError that
// otherwise crashes to the Sentry ErrorBoundary. Strategy: re-import once for a
// transient blip, then hard-reload once to pull the fresh index + chunk hashes.
// A genuinely-missing chunk can't loop (cooldown) and a blocked reload can't
// hang (grace timeout) — both fall through to the error boundary.
export function createChunkRetryLoader(importer) {
  return async () => {
    try {
      return await importer();
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;

      try {
        return await importer();
      } catch (retryError) {
        if (!isChunkLoadError(retryError)) throw retryError;

        const now = Date.now();
        if (now - readReloadAt() < RELOAD_COOLDOWN_MS) throw retryError;
        markReloadAt(now);
        window.location.reload();

        return new Promise((_resolve, reject) => {
          setTimeout(() => reject(retryError), RELOAD_GRACE_MS);
        });
      }
    }
  };
}

export function lazyWithRetry(importer) {
  return lazy(createChunkRetryLoader(importer));
}
